import { IJsonQuery } from '@cian/api-models/common/json_query';
import {
  combineReducers,
  compose,
  createStore as _createStore,
  applyMiddleware,
  Action,
  AnyAction,
  Reducer,
} from 'redux';
// tslint:disable-next-line:match-default-export-name
import thunkMiddleware from 'redux-thunk';

import { oldReducers } from '../../../app/redux/reducers';
import {
  IReduxState,
  TReduxActions,
  IReduxStore,
  TThunkMiddleware,
  TThunkDispatch,
  IThunkContext,
} from '../../types/redux';
import { IFiltersState } from '../../types/filters';
import { IPageState } from '../../types/page';
import { IFeaturesState } from '../../types/features';
import { defaultState as pageDefaultState } from '../../constants/page';
import {
  setJsonQueryReducer,
  selectOfferTypeReducer,
  selectRoomTypeReducer,
  setPriceReducer,
  setAreaReducer,
  setLandAreaReducer,
  setPosessorReducer,
  metaReducer,
  setDealTypeReducer,
  setBedroomsCountReducer,
  setRegionReducer,
  setVillageReducer,
  setBSCentersReducer,
  suggestionsReducer,
  selectGeoReducer,
  geocodeReducer,
  clearGeoReducer,
  clearReducer,
} from '../../reducers';
import { createSimpleReducer } from './createSimpleReducer';
import { mortgageFilters } from '../../reducers/mortgage';

let storeReminder: IReduxStore;

export interface ICreateReduxStoreOptions {
  context: IThunkContext;
  initialState?: Partial<IReduxState>;
  debugEnabled?: boolean;
}

const dobroshriftDefaultState = { isDobroshriftEnabled: false };

export function createReduxStore(options: ICreateReduxStoreOptions) {
  const reducers = combineReducers<IReduxState, TReduxActions>({
    ...oldReducers,
    filters: combineReducers<IFiltersState, TReduxActions>({
      jsonQuery: reduceReducers<IJsonQuery, TReduxActions>(
        setJsonQueryReducer,
        setDealTypeReducer,
        selectOfferTypeReducer,
        selectRoomTypeReducer,
        setPriceReducer,
        setAreaReducer,
        setLandAreaReducer,
        setPosessorReducer,
        setBedroomsCountReducer,
        setRegionReducer,
        setVillageReducer,
        setBSCentersReducer,
        selectGeoReducer,
        clearGeoReducer,
        /** Всегда последний */
        clearReducer,
      ),
      meta: metaReducer,
      suggestions: suggestionsReducer,
      geocode: geocodeReducer,
    }),
    page: createSimpleReducer<IPageState>(pageDefaultState),
    features: createSimpleReducer<IFeaturesState>(dobroshriftDefaultState),
    mortgage: mortgageFilters,
  });

  const composeEnhancers = options.debugEnabled
    ? window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__ || compose
    : compose;

  const storeEnchancers = composeEnhancers(
    applyMiddleware(
      thunkMiddleware.withExtraArgument(options.context) as TThunkMiddleware,
    ),
  );

  let createStore: () => IReduxStore;
  if (options.initialState) {
    createStore = () => _createStore<IReduxState, TReduxActions, { dispatch: TThunkDispatch }, {}>(
      reducers,
      options.initialState as IReduxState,
      storeEnchancers,
    );
  } else {
    createStore = () => _createStore<IReduxState, TReduxActions, { dispatch: TThunkDispatch }, {}>(
      reducers,
      storeEnchancers,
    );
  }

  if (process.env.NODE_ENV === 'development' && options.debugEnabled) {
    if (!storeReminder) {
      storeReminder = createStore();
    }

    storeReminder.replaceReducer(reducers);

    return storeReminder;
  }

  return createStore();
}

// tslint:disable-next-line:no-any
export function reduceReducers<S, A extends Action<any> = AnyAction>(...reducers: Reducer<S, A>[]): Reducer<S, A> {
  return (prevState: S | undefined, action: A): S =>
    reducers.reduce(
      (newState, reducer) => reducer(newState, action),
      prevState,
    ) as S;
}
